import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
    SparklesIcon,
    PlusIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    ArrowPathIcon,
    ChevronLeftIcon,
    QueueListIcon,
    BeakerIcon,
    CommandLineIcon,
    ListBulletIcon,
    LinkIcon,
    IdentificationIcon
} from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { coreService } from '../services/coreService';

const stripHtml = (html: string) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { userStoryService, UserStory } from '../services/userStoryService';
import { Slider } from '../components/ui/Slider';
import { SyncButton } from '../components/SyncButton';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

export const UserStoriesPage: React.FC = () => {
    const { requirementId } = useParams<{ requirementId: string }>();
    const navigate = useNavigate();
    const { user } = useSelector((state: RootState) => state.auth);
    const activeProject = useSelector((state: RootState) => state.projects.activeProject);
    const projectId = activeProject?.id;
    const isPro = user?.subscribed === 'S' || user?.plan === 'premier' || user?.plan === 'enterprise';

    const [userStories, setUserStories] = useState<UserStory[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [generatingTasks, setGeneratingTasks] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [extraContext, setExtraContext] = useState('');
    const [showContextInput, setShowContextInput] = useState(false);
    const [isRegenerateMode, setIsRegenerateMode] = useState(false);
    const [storyCount, setStoryCount] = useState(5);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newStory, setNewStory] = useState({
        title: '',
        description: '',
        priority: 'medium',
        storyPoints: 3,
        acceptanceCriteria: ''
    });
    const [creating, setCreating] = useState(false);
    const [isEnhancing, setIsEnhancing] = useState(false);

    const [showProgressModal, setShowProgressModal] = useState(false);
    const [progressSteps, setProgressSteps] = useState<{ label: string; status: 'completed' | 'loading' | 'pending' | 'success' | 'error' }[]>([]);

    useEffect(() => {
        if (requirementId) {
            loadUserStories();
        }
    }, [requirementId]);

    const loadUserStories = async () => {
        try {
            setLoading(true);
            const response = await userStoryService.getUserStories({ requirementId });
            setUserStories(response.data.data || []);
        } catch (err: any) {
            setError(err.message || 'Failed to load user stories');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUserStory = async () => {
        if (!requirementId || !newStory.title || !newStory.description) return;

        try {
            setCreating(true);

            // We need the project ID. Typically this would be available or we'd fetch the requirement first.
            // For now, let's assuming the backend can infer it or we fetch the requirement.
            // Actually, the backend create method (UserStoryController.store) expects projectId.
            // We might need to fetch the requirement to get the projectId if it's not in the URL.
            // But wait, the stores rely on reqId. Let's assume the backend handles it or we pass what we have.
            // Looking at UserStoryService.ts/UserStoryController.ts:
            // Controller.store: const userStory = await UserStory.create({...data, createdBy: user.id...})
            // It expects data to contain what's needed.
            // We should probably fetch the requirement details to get the projectId. 
            // OR the backend should validate/fill it.
            // Let's pass requirementId.

            await userStoryService.createUserStory({
                ...newStory,
                requirementId,
                status: 'backlog',
                priority: newStory.priority as any,
                acceptanceCriteria: newStory.acceptanceCriteria.split('\n').filter(line => line.trim() !== '')
            });

            setIsCreateModalOpen(false);
            setNewStory({ title: '', description: '', priority: 'medium', storyPoints: 3, acceptanceCriteria: '' });
            await loadUserStories();
        } catch (err: any) {
            setError(err.message || 'Failed to create user story');
        } finally {
            setCreating(false);
        }
    };

    const handleGenerateUserStories = async (regenerate: boolean = false) => {
        if (!requirementId) return;

        try {
            setGenerating(true);
            setShowProgressModal(true);
            setError(null);

            // Initialize progress steps
            setProgressSteps([
                { label: 'Skipped research phase - no tools were available', status: 'completed' },
                { label: 'Generating User Stories for PRD...', status: 'loading' }
            ]);



            const response = await userStoryService.generateUserStories({
                requirementId,
                extraContext: extraContext || undefined,
                regenerate,
                count: storyCount
            });

            if (response.success) {
                // Show completion state briefly
                setProgressSteps(prev => prev.map(s => s.status === 'loading' ? { ...s, status: 'completed' } : s));

                await new Promise(resolve => setTimeout(resolve, 800));

                await loadUserStories();
                setShowContextInput(false);
                setIsRegenerateMode(false);
                setExtraContext('');
                setShowProgressModal(false);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to generate user stories');
            setShowProgressModal(false);
        } finally {
            setGenerating(false);
        }
    };

    const handleEnhanceNewStory = async () => {
        if (!newStory.description) return;
        setIsEnhancing(true);
        try {
            const result = await coreService.enhance({
                content: newStory.description,
                instruction: "Enhance this user story description to be more professional, specific, and clear. Ensure it follows the 'As a... I want... So that...' format if appropriate.",
                context: {
                    title: newStory.title
                }
            });

            if (result.success && result.data.editedContent) {
                setNewStory(prev => ({ ...prev, description: result.data.editedContent }));
            }
        } catch (err: any) {
            console.error('Enhancement failed:', err);
            setError('AI Enhancement failed. Please try again.');
        } finally {
            setIsEnhancing(false);
        }
    };

    const handleGenerateTasks = async (userStoryId: string) => {
        try {
            setGeneratingTasks(userStoryId);
            const response = await userStoryService.generateTasks(userStoryId);

            if (response.success) {
                await loadUserStories();
            }
        } catch (err: any) {
            setError(err.message || 'Failed to generate tasks');
        } finally {
            setGeneratingTasks(null);
        }
    };

    const getStatusColor = (status: string) => {
        const colors = {
            backlog: 'bg-gray-100 text-gray-700',
            in_progress: 'bg-blue-100 text-blue-700',
            review: 'bg-yellow-100 text-yellow-700',
            done: 'bg-green-100 text-green-700',
            blocked: 'bg-red-100 text-red-700'
        };
        return colors[status as keyof typeof colors] || colors.backlog;
    };

    const getPriorityColor = (priority: string) => {
        const colors = {
            low: 'text-gray-600',
            medium: 'text-yellow-600',
            high: 'text-red-600'
        };
        return colors[priority as keyof typeof colors] || colors.medium;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen relative overflow-hidden bg-[#0B0F19]">
                <div className="absolute inset-0 bg-primary/5 blur-[120px] rounded-full animate-pulse-subtle"></div>
                <ArrowPathIcon className="h-8 w-8 animate-spin text-primary relative z-10" />
            </div>
        );
    }

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6 relative z-10">
            <LoadingOverlay 
                isOpen={generating || !!generatingTasks || isEnhancing} 
                message={
                    generating ? "Generating user stories with AI engine..." :
                    generatingTasks ? "Generating user story tasks with AI engine..." :
                    "Enhancing story details with AI..."
                } 
            />
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div className="relative flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-1.5 hover:bg-white/10 rounded-lg text-text-secondary hover:text-white transition-colors flex items-center justify-center group"
                        title="Back"
                    >
                        <ChevronLeftIcon className="h-6 w-6 group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <div>
                        <div className="absolute -left-6 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-transparent rounded-r-full"></div>
                        <h1 className="text-3xl font-black text-white tracking-tight">Active User Stories</h1>
                        <p className="text-text-secondary mt-1 font-medium text-sm">
                            AI-generated user stories synthesized from initial requirements
                        </p>
                    </div>
                </div>

                <div className="flex gap-4">
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        className="rounded-xl text-text-secondary hover:text-white hover:bg-white/10 flex items-center gap-1.5 px-4 transition-all font-black tracking-widest text-[10px] uppercase h-11"
                    >
                        <ChevronLeftIcon className="h-4 w-4" />
                        ABORT TO PRD
                    </Button>

                    {userStories.length > 0 && projectId && (
                        <div className="flex gap-2 mr-2 border-r border-white/10 pr-4 items-center">
                            <span className="text-[10px] font-black text-primary uppercase tracking-widest mr-1">Tether:</span>
                            <SyncButton projectId={projectId} provider="github" payload={{ type: 'user_story', items: userStories }} label="GitHub" />
                            <SyncButton projectId={projectId} provider="notion" payload={{ type: 'user_story', items: userStories }} label="Notion" />
                            <SyncButton projectId={projectId} provider="jira" payload={{ type: 'user_story', items: userStories }} label="Jira" />
                            {/* <SyncButton projectId={projectId} provider="asana" payload={{ type: 'user_story', items: userStories }} label="Asana" /> */}
                        </div>
                    )}

                    <Button
                        variant="outline"
                        onClick={() => {
                            setIsRegenerateMode(false);
                            setShowContextInput(!showContextInput);
                        }}
                        disabled={generating}
                        className="flex items-center gap-2 rounded-xl border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 hover:text-white font-black tracking-widest text-[10px] uppercase h-11 px-6 shadow-[0_0_15px_rgba(255,107,0,0.15)] backdrop-blur-md transition-all"
                    >
                        <SparklesIcon className="h-4 w-4" />
                        {userStories.length === 0 ? 'SYNTHESIZE' : 'AUGMENT'}
                    </Button>

                    <Button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="flex items-center gap-2 bg-primary hover:bg-orange-500 text-white rounded-xl shadow-[0_0_20px_rgba(255,107,0,0.4)] px-6 h-11 transition-all active:scale-95 font-black tracking-widest text-[10px] uppercase border-none"
                    >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        INJECT
                    </Button>

                    {userStories.length > 0 && (
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setIsRegenerateMode(true);
                                setShowContextInput(true);
                            }}
                            disabled={generating}
                            className="flex items-center justify-center h-11 w-11 text-text-secondary hover:text-primary hover:bg-primary/10 rounded-xl transition-all border border-transparent hover:border-primary/30"
                            title="Regenerate All"
                        >
                            <ArrowPathIcon className={`h-5 w-5 ${generating ? 'animate-spin text-primary' : ''}`} />
                        </Button>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-4 text-sm font-black uppercase tracking-widest">
                    {error}
                </div>
            )}

            {/* Context Input */}
            {showContextInput && (
                <div className="mb-6 glass-panel rounded-2xl border border-white/5 p-6 relative overflow-hidden backdrop-blur-xl">
                    <div className="absolute inset-0 bg-primary/5 blur-[50px] rounded-full z-0 pointer-events-none"></div>
                    <div className="relative z-10">
                        <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-3">
                            {isRegenerateMode ? 'Regeneration Thoughts / Feedback (Optional)' : 'Synthesis Context Parameters (Optional)'}
                        </label>
                        <textarea
                            className="w-full px-4 py-3 border border-white/10 bg-black/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 mb-6 text-white text-sm font-medium placeholder-text-secondary/30 shadow-inner resize-none min-h-[100px]"
                            placeholder={isRegenerateMode ? "Provide your thoughts, vision, or feedback on previous output..." : "Input operational constraints or specific focal areas..."}
                            value={extraContext}
                            onChange={(e) => setExtraContext(e.target.value)}
                        />

                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-3">
                                <label className="text-[11px] font-black text-text-secondary uppercase tracking-widest">Target Generation Quota</label>
                                <span className="bg-primary/10 text-primary px-3 py-1 rounded-lg text-xs font-black border border-primary/30 shadow-inner">
                                    {storyCount}
                                </span>
                            </div>
                            <div className="px-1">
                                <Slider
                                    value={storyCount}
                                    min={1}
                                    max={20}
                                    step={1}
                                    onChange={setStoryCount}
                                />
                            </div>
                            {!isPro && (
                                <div className="flex items-center gap-2 text-[10px] font-black text-orange-400 uppercase tracking-widest mt-4 bg-orange-500/10 p-3 rounded-lg border border-orange-500/30">
                                    <SparklesIcon className="h-4 w-4" />
                                    STARTER CLASS: MAX 20 STORIES
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowContextInput(false);
                                    setIsRegenerateMode(false);
                                    setExtraContext('');
                                }}
                                className="rounded-xl border-white/10 bg-white/5 text-white hover:bg-white/10 font-black tracking-widest text-[10px] uppercase h-11 px-6"
                            >
                                ABORT
                            </Button>
                            <Button
                                onClick={() => handleGenerateUserStories(isRegenerateMode)}
                                disabled={generating}
                                className="bg-primary hover:bg-orange-500 text-white shadow-[0_0_20px_rgba(255,107,0,0.3)] rounded-xl font-black tracking-widest text-[10px] uppercase h-11 px-8 border-none flex items-center gap-2"
                            >
                                {generating ? (
                                    <>
                                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                        SYNTHESIZING...
                                    </>
                                ) : (
                                    <>
                                        <SparklesIcon className="h-4 w-4" />
                                        COMMENCE SYNTHESIS
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* User Stories List */}
            {userStories.length === 0 && !generating ? (
                <div className="glass-panel p-16 text-center rounded-2xl border border-white/5 relative overflow-hidden">
                    <div className="absolute inset-0 bg-primary/5 blur-[80px] rounded-full z-0 pointer-events-none"></div>
                    <div className="relative z-10 w-40 h-40 mx-auto mb-8">
                        <img src="/DocSprintLogo.png" alt="DocSprintAI" className="w-full h-full object-contain opacity-40 grayscale" />
                    </div>
                    <h3 className="text-xl font-black text-white tracking-tight mb-3">
                        NULL MATRIX
                    </h3>
                    <p className="text-text-secondary text-sm font-medium mb-4 max-w-md mx-auto">
                        Awaiting initialization. Trigger generation or inject manually to begin populating the requirement framework.
                    </p>
                </div>
            ) : (
                <div className="space-y-6">
                    {userStories.map((story, index) => {
                        let criteriaList: string[] = [];
                        try {
                            if (Array.isArray(story.acceptanceCriteria)) {
                                criteriaList = story.acceptanceCriteria;
                            } else if (typeof story.acceptanceCriteria === 'string') {
                                if (story.acceptanceCriteria.startsWith('[')) {
                                    criteriaList = JSON.parse(story.acceptanceCriteria);
                                } else if (story.acceptanceCriteria.startsWith('{') && story.acceptanceCriteria.endsWith('}')) {
                                    const matches = story.acceptanceCriteria.match(/"((?:[^"\\]|\\.)*)"/g);
                                    if (matches) {
                                        criteriaList = matches.map(m => JSON.parse(m));
                                    } else {
                                        criteriaList = story.acceptanceCriteria.slice(1, -1).split(',').filter(Boolean);
                                    }
                                } else {
                                    criteriaList = [story.acceptanceCriteria];
                                }
                            }
                        } catch (e) {
                            criteriaList = typeof story.acceptanceCriteria === 'string' ? [story.acceptanceCriteria] : [];
                        }

                        return (
                            <div key={story.id} className="glass-panel p-7 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,107,0,0.1)] border border-white/5 hover:border-primary/40 rounded-2xl group relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>

                                {/* Header Row */}
                                <div className="flex justify-between items-start mb-5 relative z-10">
                                    <div className="space-y-2">
                                        <h3 className="text-[10px] font-black text-primary uppercase tracking-widest bg-primary/10 px-2 py-1 rounded-md border border-primary/20 w-fit">US-{index + 1}</h3>
                                        <h2 className="text-xl font-black text-white leading-tight uppercase tracking-tight group-hover:text-primary transition-colors">{story.title}</h2>
                                        <p className="text-sm text-text-secondary line-clamp-3 mt-3 font-medium max-w-4xl">{stripHtml(story.description)}</p>
                                    </div>
                                    <div className="flex gap-3 flex-shrink-0 ml-4 pt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-primary bg-primary/10 border-primary/30 hover:bg-primary/20 hover:text-white h-10 text-[10px] font-black uppercase tracking-widest px-4 rounded-xl shadow-inner transition-all hover:scale-105"
                                            onClick={() => navigate(`/user-stories/${story.id}`)}
                                        >
                                            <QueueListIcon className="h-4 w-4 mr-2" />
                                            Tasks
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-text-secondary bg-white/5 border-white/10 hover:text-white hover:bg-white/10 h-10 text-[10px] font-black uppercase tracking-widest px-4 rounded-xl transition-all"
                                            onClick={() => navigate(`/user-stories/${story.id}/test-cases`)}
                                        >
                                            <BeakerIcon className="h-4 w-4 mr-2 text-info" />
                                            Test Matrix
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-text-secondary hover:text-white hover:bg-white/10 h-10 w-10 p-0 rounded-xl transition-all border border-transparent hover:border-white/10"
                                            title="Copy Flow"
                                        >
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                        </Button>
                                    </div>
                                </div>

                                {/* Acceptance Criteria */}
                                <div className="mt-8 pt-6 border-t border-white/5 relative z-10">
                                    <h4 className="text-[11px] font-black text-text-secondary uppercase tracking-widest mb-4">
                                        Acceptance Logic Gates:
                                    </h4>
                                    <ul className="space-y-3">
                                        {criteriaList.length > 0 ? (
                                            criteriaList.map((criterion, idx) => (
                                                <li key={idx} className="text-sm text-text-secondary flex gap-3 items-start p-3 bg-black/20 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
                                                    <span className="text-primary font-black text-[10px] uppercase tracking-widest mt-0.5 bg-primary/10 px-1.5 py-0.5 rounded border border-primary/20">LOGIC-{String(idx + 1).padStart(2, '0')}</span>
                                                    <span className="leading-relaxed font-medium">{criterion}</span>
                                                </li>
                                            ))
                                        ) : (
                                            <div className="text-center py-10 bg-black/40 rounded-xl border border-dashed border-white/10">
                                                <p className="text-sm text-text-secondary/50 font-black uppercase tracking-widest">Null Logic Discovered</p>
                                            </div>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Generation Progress Modal */}
            {showProgressModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
                    <div className="glass-panel rounded-[2rem] shadow-[0_0_80px_rgba(0,0,0,0.6)] border border-white/10 p-10 max-w-md w-full animate-in zoom-in-95 duration-300 relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50"></div>
                        <div className="absolute inset-0 bg-primary/10 blur-[80px] rounded-full z-0 pointer-events-none"></div>

                        <div className="relative z-10">
                            <h3 className="text-2xl font-black text-white tracking-tight uppercase mb-8 text-center text-primary drop-shadow-[0_0_15px_rgba(255,107,0,0.4)]">Generating Stories...</h3>
                            <div className="space-y-6">
                                <div className="flex items-center gap-4 text-sm font-black text-white uppercase tracking-widest bg-white/5 p-4 rounded-xl border border-white/10">
                                    <span className="h-2.5 w-2.5 rounded-full bg-info shadow-[0_0_10px_rgba(59,130,246,0.6)] animate-pulse"></span>
                                    Compiling Stories...
                                </div>

                                {progressSteps.map((step, idx) => (
                                    <div key={idx} className="flex items-center gap-4">
                                        {step.status === 'completed' || step.status === 'success' ? (
                                            <CheckCircleIcon className="h-6 w-6 text-emerald-500 drop-shadow-[0_0_10px_rgba(16,185,129,0.4)]" />
                                        ) : step.status === 'error' ? (
                                            <XCircleIcon className="h-6 w-6 text-red-500 drop-shadow-[0_0_10px_rgba(239,68,68,0.4)]" />
                                        ) : step.status === 'loading' ? (
                                            <ArrowPathIcon className="h-6 w-6 text-primary animate-spin" />
                                        ) : (
                                            <div className="h-6 w-6 rounded-full border-2 border-white/10"></div>
                                        )}
                                        <span className={`text-[11px] font-black uppercase tracking-widest ${step.status === 'loading' ? 'text-primary' : step.status === 'completed' || step.status === 'success' ? 'text-emerald-500' : step.status === 'error' ? 'text-red-500' : 'text-text-secondary/50'}`}>
                                            {step.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Story Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="glass-panel rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.6)] border border-white/10 w-full max-w-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200 relative">
                        {/* Top Accent Bar */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50 z-20"></div>

                        <div className="p-6 border-b border-white/5 bg-black/40 flex justify-between items-center relative z-10 backdrop-blur-xl">
                            <div className="flex items-center gap-4">
                                <div className="p-2.5 rounded-xl bg-orange-500/10 text-orange-400 border border-orange-500/20">
                                    <QueueListIcon className="h-5 w-5" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black text-white tracking-tight uppercase">Manual Entry</h2>
                                    <p className="text-[10px] text-text-secondary/70 font-black uppercase tracking-widest mt-1">Manual Requirement Definition</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsCreateModalOpen(false)}
                                className="text-text-secondary hover:text-white hover:bg-white/10 p-2 rounded-xl transition-colors"
                            >
                                <span className="sr-only">Close</span>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-8 space-y-8 overflow-y-auto max-h-[70vh] relative z-10 custom-scrollbar">
                            <div className="space-y-3">
                                <label className="text-[11px] font-black text-text-secondary uppercase tracking-widest flex items-center gap-2">
                                    Identity Handle <span className="text-primary">*</span>
                                </label>
                                <Input
                                    required
                                    value={newStory.title}
                                    onChange={(e) => setNewStory({ ...newStory, title: e.target.value })}
                                    placeholder="E.g., USER-AUTH-FLOW-001"
                                    className="bg-black/40 border-white/10 focus:border-primary/50 focus:ring-primary/50 text-sm font-medium text-white placeholder-text-secondary/30 shadow-inner"
                                />
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <label className="text-[11px] font-black text-text-secondary uppercase tracking-widest flex items-center gap-2">
                                        Business Logic <span className="text-primary">*</span>
                                    </label>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={handleEnhanceNewStory}
                                        disabled={isEnhancing || !newStory.description}
                                        className="h-8 text-[10px] text-primary hover:text-orange-400 hover:bg-primary/10 border border-primary/20 font-black uppercase tracking-widest flex items-center gap-2 px-3 rounded-lg transition-all"
                                    >
                                        <SparklesIcon className={`h-3 w-3 ${isEnhancing ? 'animate-spin' : ''}`} />
                                        {isEnhancing ? 'OPTIMIZING...' : 'AI OPTIMIZE'}
                                    </Button>
                                </div>
                                <div className="border border-white/10 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary/50 transition-all shadow-inner bg-black/40">
                                    <ReactQuill
                                        theme="snow"
                                        value={newStory.description}
                                        onChange={(content) => setNewStory({ ...newStory, description: content })}
                                        placeholder="As a [persona], I require [logic] ensuring [outcome]..."
                                        className="story-editor"
                                        modules={{
                                            toolbar: [
                                                [{ 'header': [1, 2, false] }],
                                                ['bold', 'italic', 'underline', 'strike'],
                                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                ['link'],
                                                ['clean']
                                            ]
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-[11px] font-black text-text-secondary uppercase tracking-widest flex items-center gap-2">
                                    Acceptance Logic Gates
                                </label>
                                <div className="border border-white/10 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary/50 transition-all shadow-inner bg-black/40">
                                    <textarea
                                        rows={4}
                                        value={newStory.acceptanceCriteria}
                                        onChange={(e) => setNewStory({ ...newStory, acceptanceCriteria: e.target.value })}
                                        className="w-full p-5 text-sm text-white leading-relaxed outline-none resize-none font-medium bg-transparent placeholder-text-secondary/30 custom-scrollbar"
                                        placeholder="One criterion per line..."
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6 bg-white/5 p-6 rounded-xl border border-white/5">
                                <div className="space-y-3">
                                    <label className="text-[11px] font-black text-text-secondary uppercase tracking-widest">Priority Vector</label>
                                    <div className="relative">
                                        <select
                                            className="w-full appearance-none rounded-xl border border-white/10 bg-black/40 px-5 py-3.5 text-sm font-black uppercase tracking-wider text-white focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all cursor-pointer hover:border-white/20 shadow-inner bg-none"
                                            value={newStory.priority}
                                            onChange={(e) => setNewStory({ ...newStory, priority: e.target.value as any })}
                                        >
                                            <option value="low">Low Priority</option>
                                            <option value="medium">Medium Priority</option>
                                            <option value="high">High Priority</option>
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-5 text-text-secondary">
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[11px] font-black text-text-secondary uppercase tracking-widest">Complexity Weight</label>
                                    <Input
                                        type="number"
                                        value={newStory.storyPoints}
                                        onChange={(e) => setNewStory({ ...newStory, storyPoints: parseInt(e.target.value) || 0 })}
                                        className="bg-black/40 border-white/10 focus:border-primary/50 focus:ring-primary/50 text-white font-black shadow-inner"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-black/40 flex justify-end gap-3 border-t border-white/5 backdrop-blur-xl relative z-10">
                            <Button
                                variant="outline"
                                onClick={() => setIsCreateModalOpen(false)}
                                className="rounded-xl border-white/10 bg-white/5 text-white hover:bg-white/10 font-black tracking-widest text-[10px] uppercase h-11 px-6"
                            >
                                ABORT
                            </Button>
                            <Button
                                className="bg-primary hover:bg-orange-500 text-white shadow-[0_0_20px_rgba(255,107,0,0.3)] font-black tracking-widest text-[10px] uppercase px-8 h-11 flex items-center gap-2 border-none"
                                onClick={handleCreateUserStory}
                                disabled={creating || !newStory.title || !newStory.description}
                            >
                                {creating ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                                {creating ? 'COMPILING...' : 'SAVE STORY'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
